import { WORLD_H, WORLD_W, type LevelDef, type PlayerState, type Point, type Rect, type WorldState } from '../../shared/types';
import { getPlatforms, isGateOpen, isHazardActive } from '../../shared/simulation';
import { CEILING_FRACTION, screenRect, type CameraView } from './camera';
import { circuitMark, clamp, disc, mix, rounded, text, INK, PAPER } from './paint';
import { drawLantern } from './lantern';
import { circuitDestinations, circuitLinkPath, drawCircuits, drawDormantBridge, drawGate, drawPlatform, drawSwitch, PALETTES } from './world';
import { describeHazard, projectorPath } from './hazards';

/** The camera ease of a consequence the local courier can reach (framing rules unchanged; it
 * now holds as long as the inset so the +1 s frame still shows the change). */
export const CAMERA_BEAT_MS = 1200;
/** The inset of a consequence out of the camera's reach, and every contributor chip. */
export const INSET_BEAT_MS = 1200;
export const CHIP_MS = 1200;
/** One pass of the travelling pulse along the circuit link from plate to consequence; it runs
 * PULSE_PASSES times (the later passes fainter) so the causal line stays readable for the beat. */
export const PULSE_MS = 600;
export const PULSE_PASSES = 2;
/** Transitions this close together are one beat. */
export const COALESCE_MS = 1000;

const ease = (n: number) => { const t = clamp(n, 0, 1); return t * t * (3 - 2 * t); };
const intersect = (a: Rect, b: Rect, gap = 0) => a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y;
const union = (rects: Rect[]): Rect => {
  const x = Math.min(...rects.map(r => r.x)), y = Math.min(...rects.map(r => r.y));
  return { x, y, w: Math.max(...rects.map(r => r.x + r.w)) - x, h: Math.max(...rects.map(r => r.y + r.h)) - y };
};
const contains = (view: CameraView, rect: Rect, padding = 4) => {
  const r = screenRect(view, rect);
  return r.x >= padding && r.y >= padding && r.x + r.w <= view.width - padding && r.y + r.h <= view.height - padding;
};
const letterOf = (level: LevelDef, plateId: string) => String.fromCharCode(65 + Math.max(0, level.switches.findIndex(s => s.id === plateId)));
const standsOn = (root: Rect, plate: Rect) => Math.abs(root.y + root.h - plate.y - plate.h) < 2 && root.x + root.w > plate.x && root.x < plate.x + plate.w;
/** The body that carries a stack: the plate counts every rider through it (authoritative supportId chain). */
function supportRoot(player: PlayerState, players: readonly PlayerState[]): PlayerState {
  let root = player;
  const visited = new Set<string>();
  while (root.supportId && !visited.has(root.id)) {
    visited.add(root.id);
    const parent = players.find(p => p.id === root.supportId && p.connected && !p.entered);
    if (!parent) break;
    root = parent;
  }
  return root;
}

interface Target { id: string; kind: 'gate' | 'platform' | 'hazard'; rect: Rect; opening: boolean; switchIds: string[]; causes: string[]; at: number }
interface Pulse { plateId: string; targetId: string; path: Point[]; cumulative: number[]; length: number; at: number }
interface Contributor { id: string; plateId: string; letter: string; at: number }
interface Beat {
  /** First transition (ease-in reference) and the latest one (the presentation ends after it). */
  at: number; lastAt: number; tick: number;
  targets: Target[];
  /** Every plate the changed routes depend on (title letters) and the ones that actually transitioned. */
  plates: string[]; causes: string[];
  contributors: Map<string, Contributor>;
  pulses: Pulse[];
  /** The changed route: targets, the transitioned plates, the contributors standing on them and
   * the circuit links between them, so the inset frames the causal line itself. */
  bounds: Rect;
  /** inset: the whole presentation is the inset (out of reach, unsafe, reduced motion, a release).
   * bridge: a camera beat also shows the inset, because a target is not (yet) inside the eased
   * frame or a later transition joined the beat; it then stays for the rest of the beat. */
  inset: boolean; bridge: boolean; decided: boolean; recheck: boolean; coalesced: number;
  /** The camera aim, fixed on the deciding frame (a per-frame re-aim would snap a settling view). A
   * coalesced change whose targets fit re-aims once, easing from the view shown at that moment. */
  aim: { region: Rect; scale: number } | null;
  reaim: { from: CameraView; at: number } | null;
}

/** One short view of a RECEIVED cooperative consequence, never a speculative switch. */
export class CooperativeFocus {
  private beat: Beat | null = null;
  private inset: Rect | null = null;
  private insetRegion: Rect | null = null;
  private slot: { rect: Rect; width: number; height: number; critical: boolean } | null = null;
  private shownView: CameraView | null = null;
  private reducedMotion = false;
  private mode: 'none' | 'camera' | 'camera+inset' | 'inset' | 'overview' = 'none';
  /** Screen rectangles other persistent layers own (the retries counter, a reset chip); the inset never covers them. */
  private avoid: readonly Rect[] = [];

  reset(): void { this.beat = null; this.inset = null; this.insetRegion = null; this.slot = null; this.shownView = null; this.mode = 'none'; }

  accept(previous: WorldState | undefined, world: WorldState, level: LevelDef, now: number): void {
    if (!previous || previous.epoch !== world.epoch || previous.levelIndex !== world.levelIndex || world.complete) { this.reset(); return; }
    const activeIn = (state: WorldState, id: string) => !!state.switches.find(s => s.id === id)?.active;
    const transitioned = level.switches.filter(plate => activeIn(previous, plate.id) !== activeIn(world, plate.id)).map(plate => plate.id);
    const targets: Target[] = [];
    const attribute = (switchIds: string[]) => { const causes = switchIds.filter(id => transitioned.includes(id)); return causes.length ? causes : [...switchIds]; };
    for (const gate of level.gates) {
      const wasOpen = isGateOpen(gate, previous), open = isGateOpen(gate, world);
      if (open === wasOpen) continue;
      targets.push({ id: gate.id, kind: 'gate', rect: { x: gate.x, y: gate.y, w: gate.w, h: gate.h }, opening: open, switchIds: [...gate.switchIds], causes: attribute(gate.switchIds), at: now });
    }
    const before = getPlatforms(level, previous), after = getPlatforms(level, world);
    for (const platform of level.platforms.filter(p => p.switchIds?.length)) {
      const was = before.some(p => p.id === platform.id), visible = after.some(p => p.id === platform.id);
      if (was === visible) continue;
      const shape = after.find(p => p.id === platform.id) ?? platform;
      targets.push({ id: platform.id, kind: 'platform', rect: { x: shape.x, y: shape.y, w: shape.w, h: shape.h }, opening: visible, switchIds: [...platform.switchIds!], causes: attribute(platform.switchIds!), at: now });
    }
    for (const hazard of level.hazards.filter(h => h.disabledBy?.length)) {
      // A plate making a hazard safe (or a hold releasing it) is a route change too; a hazard's
      // own timed cycle is not a cooperative consequence and never starts a beat.
      const wasHeld = describeHazard(hazard, level, previous).held, held = describeHazard(hazard, level, world).held;
      if (wasHeld === held) continue;
      targets.push({ id: hazard.id, kind: 'hazard', rect: { x: hazard.x, y: hazard.y, w: hazard.w, h: hazard.h }, opening: held, switchIds: [...hazard.disabledBy!], causes: attribute(hazard.disabledBy!), at: now });
    }
    if (!targets.length) return;

    const plates = [...new Set(targets.flatMap(t => t.switchIds))];
    const causes = [...new Set(targets.flatMap(t => t.causes))];
    const bodies = world.players.filter(player => player.connected && !player.entered);
    // Contributors: bodies counted on a plate the opened route depends on, per authoritative
    // state (a rider counts through the body carrying it). A release credits nobody.
    const contributors: Contributor[] = [];
    const openPlates = [...new Set(targets.filter(t => t.opening).flatMap(t => t.switchIds))];
    for (const player of bodies) {
      const root = supportRoot(player, bodies);
      if (!root.grounded) continue;
      const plate = level.switches.find(candidate => openPlates.includes(candidate.id) && standsOn(root, candidate));
      if (plate) contributors.push({ id: player.id, plateId: plate.id, letter: letterOf(level, plate.id), at: now });
    }
    const pulses: Pulse[] = [];
    for (const target of targets) {
      // The causal line: every lit plate feeding an opened route, or the plate whose release closed it.
      const from = target.opening ? target.switchIds.filter(id => activeIn(world, id) || target.causes.includes(id)) : target.causes;
      for (const plateId of from) {
        const index = level.switches.findIndex(s => s.id === plateId);
        const plate = level.switches[index];
        if (!plate) continue;
        const hazard = target.kind === 'hazard' ? level.hazards.find(h => h.id === target.id) : undefined;
        const destination = circuitDestinations(level, world, plate, true).find(d => d.targetId === target.id);
        const path = hazard?.kind === 'beam' && target.opening ? projectorPath(plate, hazard) : destination ? circuitLinkPath(plate, index, destination) : null;
        if (!path) continue;
        const cumulative = [0];
        for (let i = 1; i < path.length; i++) cumulative.push(cumulative[i - 1] + Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y));
        pulses.push({ plateId, targetId: target.id, path, cumulative, length: cumulative[cumulative.length - 1], at: now });
      }
    }
    const causePlates = level.switches.filter(plate => causes.includes(plate.id));
    const onCause = contributors.filter(c => causes.includes(c.plateId)).map(c => bodies.find(p => p.id === c.id)!);
    const wiring = pulses.filter(pulse => causes.includes(pulse.plateId)).flatMap(pulse => pulse.path.map(point => ({ x: point.x, y: point.y, w: 0, h: 0 })));
    const bounds = union([...targets.map(t => t.rect), ...causePlates, ...onCause, ...wiring]);

    const current = this.beat;
    if (current && now - current.at < COALESCE_MS && now < this.beatEnd(current)) {
      // Coalesce: one beat carries every transition of the last second.
      for (const target of targets) {
        const index = current.targets.findIndex(t => t.id === target.id);
        if (index >= 0) current.targets[index] = target; else current.targets.push(target);
      }
      current.plates = [...new Set([...current.plates, ...plates])];
      current.causes = [...new Set([...current.causes, ...causes])];
      for (const contributor of contributors) current.contributors.set(contributor.id, contributor);
      current.pulses.push(...pulses);
      current.bounds = union([current.bounds, bounds]);
      current.lastAt = now;
      current.tick = world.tick;
      // A decided camera beat re-aims (if the joined change fits) or shows it in the inset beside it.
      if (current.decided) current.recheck = true; else current.inset ||= targets.some(t => !t.opening);
      current.coalesced++;
      return;
    }
    this.beat = { at: now, lastAt: now, tick: world.tick, targets, plates, causes, contributors: new Map(contributors.map(c => [c.id, c])), pulses,
      bounds, inset: targets.some(t => !t.opening), bridge: false, decided: false, recheck: false, coalesced: 0, aim: null, reaim: null };
    this.inset = null;
    this.insetRegion = null;
    this.slot = null;
  }

  private viewEnd(beat: Beat): number { return beat.lastAt + (beat.inset ? INSET_BEAT_MS : CAMERA_BEAT_MS); }
  private beatEnd(beat: Beat): number {
    return Math.max(this.viewEnd(beat), ...[...beat.contributors.values()].map(c => c.at + CHIP_MS), ...beat.pulses.map(p => p.at + PULSE_MS * PULSE_PASSES));
  }

  /** The local courier with everybody carrying them or riding on them (authoritative supportId links). */
  private chain(players: readonly PlayerState[], localId: string | null): PlayerState[] {
    const alive = players.filter(p => p.connected && !p.entered);
    const local = alive.find(p => p.id === localId);
    if (!local) return [];
    const linked = new Set<string>([local.id]);
    for (let pass = 0; pass < alive.length; pass++) {
      for (const player of alive) {
        if (linked.has(player.id) && player.supportId && alive.some(p => p.id === player.supportId)) linked.add(player.supportId);
        if (player.supportId && linked.has(player.supportId)) linked.add(player.id);
      }
    }
    return alive.filter(p => linked.has(p.id));
  }

  /** The changed route plus the contributors who stand on the plates that changed it. */
  private region(beat: Beat, players: readonly PlayerState[]): Rect {
    const causeBodies = players.filter(p => p.connected && !p.entered && beat.causes.includes(beat.contributors.get(p.id)?.plateId ?? ''));
    return union([beat.bounds, ...causeBodies]);
  }

  apply(base: CameraView, players: PlayerState[], world: WorldState, level: LevelDef, localId: string | null, now: number, reducedMotion: boolean, avoid: readonly Rect[] = []): CameraView {
    const beat = this.beat;
    const previousShown = this.shownView;
    this.reducedMotion = reducedMotion;
    this.avoid = avoid;
    this.inset = null; this.insetRegion = null; this.mode = 'none'; this.shownView = base;
    if (!beat) return base;
    if (now >= this.beatEnd(beat)) { this.beat = null; return base; }
    if (now >= this.viewEnd(beat)) return base; // chips/pulses may still be running
    // The whole trail already shows every consequence; only the gate rise animates.
    if (!base.follow) { this.mode = 'overview'; return base; }
    const bodies = players.filter(p => p.connected && !p.entered && (base.teamIds?.includes(p.id) || p.id === localId));
    const route = this.region(beat, players);
    const bounds = union([route, ...bodies]);
    const region = { x: bounds.x - 14, y: Math.max(0, bounds.y - 14), w: bounds.w + 28, h: bounds.h + 28 };
    const scale = Math.min(base.scale, (base.width - 16) / region.w, (base.height - 16) / region.h);
    if (!beat.decided) {
      // Decide the presentation ONCE, on the first frame after the received change.
      // A mid-beat jump then never flips a settling camera into an inset, or back.
      const local = players.find(p => p.id === localId && p.connected && !p.entered);
      const root = local ? supportRoot(local, players) : undefined;
      const unsafe = !local?.grounded || !root?.grounded || level.hazards.some(h => isHazardActive(h, world) && local && intersect(local, h, 48));
      if (unsafe || reducedMotion) beat.inset = true;
      if (scale < base.scale * .72) beat.inset = true;
      // Keep an already visible consequence still (no ceremonial pan on a wide screen); otherwise
      // aim once, here: a per-frame re-aim would snap the view when the team or the base frame moves.
      const still = [...beat.targets.map(t => t.rect), ...level.switches.filter(s => beat.causes.includes(s.id))].every(r => contains(base, r, 8));
      beat.aim = still ? null : { region, scale };
      beat.decided = true;
    } else if (beat.recheck) {
      beat.recheck = false;
      const shown = previousShown ?? base;
      if (!beat.inset && scale >= base.scale * .72 && beat.targets.some(t => !contains(shown, t.rect, 0))) {
        // The joined change fits: glide from the view shown right now to the new aim.
        beat.aim = { region, scale };
        beat.reaim = { from: shown, at: now };
      }
    }
    if (beat.inset) {
      this.insetRegion = route;
      this.inset = this.findInset(base, players, level, world, localId, route);
      this.mode = this.inset ? 'inset' : 'none';
      return base;
    }
    this.mode = 'camera';
    let view = base;
    if (beat.aim) {
      const aim = beat.aim;
      const age = now - beat.at;
      const amount = ease(age / 130) * ease((this.viewEnd(beat) - now) / 160);
      const s = mix(base.scale, aim.scale, amount);
      const targetX = clamp(aim.region.x + aim.region.w / 2 - base.width / aim.scale / 2, 0, Math.max(0, WORLD_W - base.width / aim.scale));
      const targetY = clamp(aim.region.y + aim.region.h / 2 - base.height / aim.scale / 2, 0, Math.max(0, WORLD_H - base.height / aim.scale));
      let x = mix(base.x, targetX, amount), y = mix(base.y, targetY, amount);
      const chain = this.chain(players, localId);
      if (chain.length) {
        // The local courier and the bodies stacked with them stay on screen throughout, the local
        // courier itself never above the top 15% of the frame (a jump during the beat).
        const team = union(chain);
        const local = chain.find(p => p.id === localId) ?? chain[0];
        x = clamp(x, team.x + team.w + 3 / s - base.width / s, team.x - 3 / s);
        y = clamp(y, team.y + team.h + 3 / s - base.height / s, Math.min(team.y - 3 / s, local.y - CEILING_FRACTION * base.height / s));
      }
      let s2 = s;
      if (beat.reaim) {
        const k = ease((now - beat.reaim.at) / 180);
        x = mix(beat.reaim.from.x, x, k); y = mix(beat.reaim.from.y, y, k); s2 = mix(beat.reaim.from.scale, s, k);
        if (k >= 1) beat.reaim = null;
      }
      const visibleW = base.width / s2, visibleH = base.height / s2;
      x = clamp(x, 0, Math.max(0, WORLD_W - visibleW));
      y = clamp(y, 0, Math.max(0, WORLD_H - visibleH));
      view = { ...base, x, y, scale: s2,
        offsetX: visibleW > WORLD_W ? (base.width - WORLD_W * s2) / 2 : 0,
        offsetY: visibleH > WORLD_H ? (base.height - WORLD_H * s2) / 2 : 0 };
      this.shownView = view;
    }
    // A consequence the eased frame does not (yet) hold is shown in the inset from the very first
    // frame; once shown, the inset stays for the rest of the beat rather than popping mid-beat.
    if (beat.targets.some(t => !contains(view, t.rect, 0))) beat.bridge = true;
    if (beat.bridge) {
      this.insetRegion = route;
      this.inset = this.findInset(view, players, level, world, localId, route);
      if (this.inset) this.mode = 'camera+inset';
    }
    return view;
  }

  /** A screen slot for the inset, sized to the changed route's own aspect ratio so it stays as
   * large as the viewport allows. Bodies and the uncollected key are never covered; a trail-map
   * slot is kept when one exists (on phones the inset leaves the map its column). The search
   * always ends with a slot: at worst a small one that avoids only the local courier and the key. */
  private findInset(view: CameraView, players: PlayerState[], level: LevelDef, world: WorldState, localId: string | null, region: Rect): Rect | null {
    const alive = players.filter(p => p.connected && !p.entered);
    const obstacles = alive.map(p => screenRect(view, p));
    const critical = alive.filter(p => p.id === localId).map(p => screenRect(view, p));
    for (const key of level.keys.filter(k => !world.collectedKeys.includes(k.id))) {
      const rect = screenRect(view, { x: key.x - 12, y: key.y - 12, w: key.w + 24, h: key.h + 24 });
      obstacles.push(rect); critical.push(rect);
    }
    // Persistent HUD chips (the retries counter) and a running reset chip are never covered either.
    for (const rect of this.avoid) { obstacles.push({ ...rect }); critical.push({ ...rect }); }
    const inside = (rect: Rect) => rect.x >= 6 && rect.y >= 6 && rect.x + rect.w <= view.width - 6 && rect.y + rect.h <= view.height - 6;
    const free = (rect: Rect, avoid: Rect[]) => inside(rect) && !avoid.some(b => intersect(rect, b, 5));
    // A slot found on an earlier frame of this beat stays while it remains clear.
    const previous = this.slot;
    if (previous && previous.width === view.width && previous.height === view.height && free(previous.rect, previous.critical ? critical : obstacles)) return previous.rect;
    const remember = (rect: Rect | null, onlyCritical = false) => { this.slot = rect ? { rect, width: view.width, height: view.height, critical: onlyCritical } : null; return rect; };
    // Mirrors the annotation layer's actual map candidates.
    const mapOptions = [view.width - 162, 8, (view.width - 154) / 2].map(x => ({ x, y: 8, w: 154, h: 100 }));
    const aspect = clamp((region.w + 20) / (region.h + 20), 0.6, 3.2);
    const heights = [...new Set([Math.min(176, Math.round(view.height * .46)), 148, 124, 100, 84])].sort((a, b) => b - a).filter(h => h <= view.height * .5 && h >= 70);
    const keepsMap = (rect: Rect) => mapOptions.some(map => !intersect(map, rect, 4) && obstacles.every(b => !intersect(map, b, 4)));
    const anyMap = mapOptions.some(map => obstacles.every(b => !intersect(map, b, 4)));
    const phone = view.width < 700;
    const sizes: { w: number; h: number }[] = [];
    for (const h of heights) {
      const fitted = clamp(Math.round((h - 26) * aspect + 10), 112, view.width - 16);
      // Phones: the trail map keeps a column at the top; a narrower, still aspect-fitted inset
      // is preferred over a wide one that would take every map slot.
      const narrow = clamp(view.width - 16 - 162 - 6, 112, fitted);
      if (phone && anyMap && narrow < fitted) sizes.push({ w: narrow, h: Math.max(70, Math.min(h, Math.round((narrow - 10) / aspect + 26))) });
      sizes.push({ w: fitted, h });
    }
    let fallback: Rect | null = null;
    for (const { w, h } of sizes) {
      const fits: Rect[] = [];
      for (const x of [8, view.width - w - 8, (view.width - w) / 2]) {
        for (const y of [8, view.height - h - 8]) {
          const rect = { x, y, w, h };
          if (free(rect, obstacles)) fits.push(rect);
        }
      }
      // Largest body-free slot first; a surviving trail-map slot breaks ties.
      const withMap = fits.find(keepsMap);
      if (withMap) return remember(withMap);
      if (fits[0] && !anyMap) return remember(fits[0]);
      fallback ??= fits[0] ?? null;
    }
    if (fallback) return remember(fallback);
    // Last resort: scan for any body-free slot, then any slot that spares only the local courier and the key.
    const small = { w: clamp(Math.round(58 * aspect + 10), 112, view.width - 16), h: 84 };
    for (const avoid of [obstacles, critical]) {
      for (const y of [8, view.height - small.h - 8, (view.height - small.h) / 2]) {
        for (let x = 8; x + small.w <= view.width - 6; x += 16) {
          const rect = { x, y, w: small.w, h: small.h };
          if (free(rect, avoid)) return remember(rect, avoid === critical);
        }
      }
    }
    return remember(null);
  }

  /** Screen space the annotation layer must leave clear: the inset, and during a
   * camera beat the changed gates/bridges themselves, so the trail map and callouts
   * never cover the consequence the beat exists to show. */
  reserved(): Rect[] {
    const rects: Rect[] = this.inset ? [{ ...this.inset }] : [];
    if ((this.mode !== 'camera' && this.mode !== 'camera+inset') || !this.beat || !this.shownView) return rects;
    const view = this.shownView;
    return [...rects, ...this.beat.targets.map(target => screenRect(view, { x: target.rect.x - 6, y: target.rect.y - 10, w: target.rect.w + 12, h: target.rect.h + 10 }))];
  }

  opening(id: string, now: number, reducedMotion: boolean): number {
    const target = this.beat?.targets.find(t => t.id === id && t.opening);
    if (reducedMotion || !target) return 1;
    return ease((now - target.at - 60) / 300);
  }

  /** Contributor chips for the annotation layer: "A ✓ YOU HELPED" on the local courier,
   * "A ✓" on every other body counted on a plate that opened the route. Each chip names the plate
   * it credits (`plateId`, `letter`) so a dense view can collapse them into one chip per plate. */
  chips(localId: string | null, now: number, reducedMotion: boolean): { playerId: string; label: string; alpha: number; plateId: string; letter: string }[] {
    const beat = this.beat;
    if (!beat) return [];
    const chips: { playerId: string; label: string; alpha: number; plateId: string; letter: string }[] = [];
    for (const contributor of beat.contributors.values()) {
      const age = now - contributor.at;
      if (age < 0 || age >= CHIP_MS) continue;
      // Legible from its first frame (no invisible fade-in), settling out over the last 220 ms.
      const alpha = reducedMotion ? 1 : (0.35 + 0.65 * ease(age / 90)) * ease((CHIP_MS - age) / 220);
      chips.push({ playerId: contributor.id, label: contributor.id === localId ? `${contributor.letter} ✓ YOU HELPED` : `${contributor.letter} ✓`, alpha,
        plateId: contributor.plateId, letter: contributor.letter });
    }
    return chips;
  }

  /** World-space pulse along the dotted circuit link (or the beam's conduit) from each lit
   * plate to the consequence, drawn after the circuits. Skipped under reduced motion. */
  drawPulse(ctx: CanvasRenderingContext2D, now: number, reducedMotion: boolean): void {
    const beat = this.beat;
    if (!beat || reducedMotion) return;
    ctx.save();
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const pulse of beat.pulses) {
      const progress = (now - pulse.at) / PULSE_MS;
      const pass = Math.floor(progress);
      if (progress < 0 || pass >= PULSE_PASSES || pulse.length <= 0) continue;
      const t = progress - pass;
      const strength = pass ? 0.6 : 1;
      const trace = (): void => { ctx.beginPath(); ctx.moveTo(pulse.path[0].x, pulse.path[0].y); for (let i = 1; i < pulse.path.length; i++) ctx.lineTo(pulse.path[i].x, pulse.path[i].y); };
      // A soft glow lights the whole causal line while the head travels it.
      ctx.globalAlpha = 0.34 * strength * (1 - t * 0.6);
      trace(); ctx.strokeStyle = '#ffd77a'; ctx.lineWidth = 7; ctx.stroke();
      const at = (distance: number): Point => {
        const d = clamp(distance, 0, pulse.length);
        let i = 1;
        while (i < pulse.cumulative.length - 1 && pulse.cumulative[i] < d) i++;
        const a = pulse.path[i - 1], b = pulse.path[i];
        const span = pulse.cumulative[i] - pulse.cumulative[i - 1];
        const f = span > 0 ? (d - pulse.cumulative[i - 1]) / span : 0;
        return { x: mix(a.x, b.x, f), y: mix(a.y, b.y, f) };
      };
      const head = ease(t) * pulse.length;
      for (let i = 6; i >= 1; i--) {
        const tail = at(head - i * 9);
        ctx.globalAlpha = (1 - i / 7) * 0.8 * strength * (1 - t * 0.5);
        disc(ctx, tail.x, tail.y, 4.2 - i * 0.45, '#ffcf66');
      }
      const point = at(head);
      ctx.globalAlpha = strength * (1 - t * 0.35);
      disc(ctx, point.x, point.y, 6.5, 'rgba(255,214,120,.55)');
      disc(ctx, point.x, point.y, 3.6, '#fff1bd');
      ctx.strokeStyle = '#b8802a'; ctx.lineWidth = 1.2; ctx.stroke();
    }
    ctx.restore();
  }

  drawInset(ctx: CanvasRenderingContext2D, level: LevelDef, world: WorldState, players: PlayerState[], localId: string | null, now: number, reducedMotion: boolean): void {
    const rect = this.inset, beat = this.beat, region = this.insetRegion;
    if (!rect || !beat || !region) return;
    const p = PALETTES[level.theme];
    ctx.save();
    rounded(ctx, rect.x, rect.y, rect.w, rect.h, 8); ctx.fillStyle = PAPER; ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke();
    const letters = beat.plates.map(id => letterOf(level, id)).join('+');
    const opening = beat.targets.some(t => t.opening);
    text(ctx, `${letters} → ROUTE ${opening ? '✓' : '✕'}`, rect.x + 10, rect.y + 13, 12, INK, 'left', 800);
    const inside = { x: rect.x + 5, y: rect.y + 26, w: rect.w - 10, h: rect.h - 31 };
    ctx.save();
    ctx.beginPath(); ctx.rect(inside.x, inside.y, inside.w, inside.h); ctx.clip();
    ctx.fillStyle = p.sky; ctx.fillRect(inside.x, inside.y, inside.w, inside.h);
    const box = region;
    const scale = Math.min(inside.w / (box.w + 20), inside.h / (box.h + 20));
    const origin = { x: inside.x + (inside.w - box.w * scale) / 2 - box.x * scale, y: inside.y + (inside.h - box.h * scale) / 2 - box.y * scale };
    const toInset = (point: Point): Point => ({ x: origin.x + point.x * scale, y: origin.y + point.y * scale });
    ctx.save();
    ctx.translate(origin.x, origin.y);
    ctx.scale(scale, scale);
    const platforms = getPlatforms(level, world);
    for (const platform of platforms) drawPlatform(ctx, platform, p);
    for (const platform of level.platforms) {
      if (platform.switchIds?.length && !platforms.some(current => current.id === platform.id)) drawDormantBridge(ctx, platform);
    }
    drawCircuits(ctx, level, world, now / 1000, reducedMotion, false);
    // The diagram's point is the changed route: an amber field marks each opened
    // gate/bridge/safe crossing (grey for a closed one) so it reads even at inset scale.
    for (const target of beat.targets) {
      const r = target.rect;
      rounded(ctx, r.x - 6, r.y - 8, r.w + 12, r.h + 12, 5);
      ctx.fillStyle = target.opening ? 'rgba(233,179,79,.32)' : 'rgba(120,120,110,.28)';
      ctx.fill();
      ctx.strokeStyle = target.opening ? '#d99a2b' : '#6f6f66'; ctx.lineWidth = 2.5 / scale; ctx.stroke();
    }
    for (const gate of level.gates) drawGate(ctx, gate, isGateOpen(gate, world), p, this.opening(gate.id, now, reducedMotion));
    level.switches.forEach((plate, i) => drawSwitch(ctx, plate, world.switches.find(s => s.id === plate.id), i, p, false));
    const helpers = players.filter(player => beat.contributors.has(player.id) && player.connected && !player.entered);
    for (const player of [...helpers].sort((a, b) => b.y - a.y)) {
      ctx.save(); ctx.beginPath(); ctx.rect(player.x, player.y, player.w, player.h); ctx.clip();
      drawLantern(ctx, { x: player.x, y: player.y, color: player.color, number: (player.color % 8) + 1, facing: player.facing, grounded: player.grounded,
        speed: player.vx, vy: player.vy, time: now / 1000, phase: player.color, reducedMotion, gameplay: true, local: player.id === localId,
        carrying: helpers.some(other => other.supportId === player.id), braced: true });
      ctx.restore();
    }
    ctx.restore();
    // Screen-scale marks: the plate letter on each changed route, and one chip per plate over
    // the contributors it counted, so the cause reads at inset size.
    for (const target of beat.targets) {
      const plates = target.causes.length ? target.causes : target.switchIds;
      plates.forEach((plateId, i) => {
        const index = level.switches.findIndex(s => s.id === plateId);
        if (index < 0) return;
        const point = toInset({ x: target.rect.x + target.rect.w / 2, y: target.rect.y + target.rect.h });
        circuitMark(ctx, point.x + (i - (plates.length - 1) / 2) * 20, clamp(point.y + 4, inside.y + 10, inside.y + inside.h - 10), index, target.opening, 8);
      });
    }
    const groups = new Map<string, PlayerState[]>();
    for (const player of helpers) {
      const letter = beat.contributors.get(player.id)!.letter;
      groups.set(letter, [...(groups.get(letter) ?? []), player]);
    }
    // Chips are laid out inside the inset and never overlap one another: a chip that would
    // cross an earlier one steps down a row, and a narrow inset shortens the wording.
    const placedChips: Rect[] = [];
    ctx.font = '800 10px "Trebuchet MS", "Avenir Next", system-ui, sans-serif';
    for (const [letter, group] of groups) {
      const top = Math.min(...group.map(g => g.y));
      const centre = toInset({ x: (Math.min(...group.map(g => g.x)) + Math.max(...group.map(g => g.x + g.w))) / 2, y: top });
      const mine = group.some(g => g.id === localId);
      const wordings = [`${letter} ✓ ${group.length > 1 ? `${group.length} HELPED` : mine ? 'YOU HELPED' : 'HELPED'}`, `${letter} ✓ ${group.length > 1 ? group.length : mine ? 'YOU' : ''}`.trim(), `${letter} ✓`];
      let label = wordings[0], w = ctx.measureText(label).width + 14;
      for (const wording of wordings) { label = wording; w = ctx.measureText(label).width + 14; if (w <= inside.w - 6) break; }
      const x = clamp(centre.x - w / 2, inside.x + 3, inside.x + inside.w - w - 3);
      let y = clamp(centre.y - 24, inside.y + 3, inside.y + inside.h - 21);
      const chip = { x, y, w, h: 18 };
      for (let step = 0; step < 4 && placedChips.some(other => intersect(chip, other, 2)); step++) chip.y = Math.min(inside.y + inside.h - 21, chip.y + 21);
      if (placedChips.some(other => intersect(chip, other, 2))) chip.y = Math.max(inside.y + 3, y - 21);
      y = chip.y;
      placedChips.push(chip);
      rounded(ctx, x, y, w, 18, 6); ctx.fillStyle = '#f6dd9a'; ctx.fill(); ctx.strokeStyle = '#b98a2f'; ctx.lineWidth = 1.3; ctx.stroke();
      text(ctx, label, x + w / 2, y + 9.5, 10, INK, 'center', 800);
    }
    ctx.restore();
    ctx.restore();
  }

  diagnostics(now: number) {
    const beat = this.beat;
    const shown = this.shownView;
    return {
      active: !!beat, mode: this.mode, durationMs: beat?.inset ? INSET_BEAT_MS : CAMERA_BEAT_MS, ageMs: beat ? now - beat.at : null,
      viewEndsInMs: beat ? this.viewEnd(beat) - now : null, authorityTick: beat?.tick ?? null, coalesced: beat?.coalesced ?? 0,
      switches: beat ? [...beat.plates] : [], causes: beat ? [...beat.causes] : [], helpers: beat ? [...beat.contributors.keys()] : [],
      contributors: beat ? [...beat.contributors.values()].map(c => ({ id: c.id, plateId: c.plateId, letter: c.letter, ageMs: now - c.at })) : [],
      targets: beat?.targets.map(target => ({ id: target.id, kind: target.kind, opening: target.opening, causes: [...target.causes], rect: { ...target.rect },
        visibleInMain: !!shown && contains(shown, target.rect, 0) })) ?? [],
      // Pulses actually drawn this frame: none under reduced motion.
      pulses: beat && !this.reducedMotion ? beat.pulses.filter(p => now - p.at >= 0 && now - p.at < PULSE_MS * PULSE_PASSES).map(p => ({ plateId: p.plateId, targetId: p.targetId, progress: (now - p.at) / PULSE_MS })) : [],
      inset: this.inset ? { ...this.inset } : null, insetRegion: this.insetRegion ? { ...this.insetRegion } : null,
      insetChips: this.inset && beat ? new Set([...beat.contributors.values()].map(c => c.letter)).size : 0,
    };
  }
}
